import { useState, useMemo } from "react"
import DataTable from "ui/DataTable"
import { getHeaders } from "./headers"
import { Dropdown } from "antd"
import { EllipsisIcon } from "icons"
import styles from "./index.module.css"
import CustomPagination from "components/common/CustomPagination"
import PropTypes from "prop-types"
import { colors } from "themes/variables"
import StatusTag from "./StatusTag"
import ReservationDetailModal from "pagesV2/pro/Bookings/ReservationDetailPage/ReservationDetailModal"
import moment from "moment"
import { useDevice } from "hooks/useDevice"
import DataTableResponsive from "ui/DataTableResponsive"
import { useDispatch, useSelector } from "react-redux"
import { getClientBookingsForGuides } from "store/bookings"
import { useParams } from "react-router-dom"
import MoreActionEllipsis from "ui/MoreActionEllipsis"
import { Text } from "ui/Typography"
import LoadMoreButton from "components/common/LoadMore"

const BookingTable = ({
  page = 1,
  pageSize = 10,
  sortKey,
  sortOrder,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  items,
  metadata,
  setIsAppend
}) => {
  const [open, setOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const { isMobile } = useDevice()
  const dispatch = useDispatch()
  const { id } = useParams()
  const { loading } = useSelector((state) => state.bookings.clientBookingForGuides)

  const handleOnCancel = () => {
    setOpen(false)
    setSelectedBookingId(null)
    dispatch(getClientBookingsForGuides(id, false, { page, per_page: pageSize }))
  }
  const bookingData = useMemo(() => {
    return items?.map((trip) => {
      const { booking_id, booking_date, amount_spent, booking_status, experience } = trip || {}
      return {
        Date: booking_date ? moment(booking_date)?.format("MM/DD/YYYY") : "",
        "Booking Id": booking_id,
        Experience: experience?.title,
        "Amount Spent": `$${Number(amount_spent).toFixed(2)}`,
        Status: (
          <div className="flex-1 text-left">
            <StatusTag status={booking_status} />
          </div>
        ),
        id: trip?.uuid
      }
    })
  }, [items])

  const dataSource = useMemo(() => {
    return items?.map((trip) => {
      const { booking_id, booking_date, amount_spent, booking_status, experience, uuid } = trip || {}
      return {
        key: uuid,
        booked_date: booking_date ? moment(booking_date)?.format("MM/DD/YYYY") : "",
        booking_id: booking_id,
        title: experience?.title,
        trip_price: `$${Number(amount_spent)?.toFixed(2)}`,
        status: <StatusTag status={booking_status} />,
        actions: (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  onClick: () => {
                    setSelectedBookingId(trip?.uuid)
                    setOpen(true)
                  }
                }
              ]
            }}
          >
            <button
              type="button"
              className="cursor-pointer ml-auto"
              aria-label="More actions"
              style={{ background: "none", border: "none", padding: 0 }}
              onClick={(e) => e.stopPropagation()} // Prevent row click
            >
              <EllipsisIcon fill={colors.textSecondaryDark} width={16} height={16} />
            </button>
          </Dropdown>
        )
      }
    })
  }, [items])

  const handleRowClick = (record) => {
    setSelectedBookingId(record?.key)
    setOpen(true)
  }
  const handleMobileItemClick = (record) => {
    setSelectedBookingId(record?.id)
    setOpen(true)
  }
  const getExtraRows = () => {
    return <Text className={`w-full ${styles.tableActionBtn}`}>Actions</Text>
  }

  const actionIcon = () => {
    return <MoreActionEllipsis className={styles.tableMoreActionBtn} />
  }

  const getTableActions = () => [
    {
      label: "View",
      color: "primary",
      variant: "outlined",
      onClick: handleMobileItemClick
    }
  ]

  const handleLoadMore = () => {
    onPageChange(page + 1)
    setIsAppend(true)
  }

  const isResultMoreThanPage = useMemo(() => metadata?.total_results > 10, [metadata?.total_results])

  return (
    <>
      {isMobile ? (
        <>
          <DataTableResponsive
            actionIcon={actionIcon()}
            extraRows={getExtraRows()}
            data={bookingData}
            onItemClick={handleMobileItemClick}
            getTableActions={getTableActions}
          ></DataTableResponsive>
          {isResultMoreThanPage && (
            <LoadMoreButton
              currentPage={page}
              totalPages={Math.ceil(metadata.total_results / pageSize)}
              loading={loading}
              onLoadMore={handleLoadMore}
              totalAvailable={metadata.total_results}
            />
          )}
        </>
      ) : (
        <>
          <div className={styles.bookingTableWrapper}>
            <DataTable
              columns={getHeaders({
                field: sortKey,
                order: sortOrder
              })}
              dataSource={dataSource}
              pagination={false}
              onChange={onSortChange}
              onRow={(record) => ({
                onClick: () => {
                  handleRowClick(record)
                },
                className: styles.clickableRow
              })}
            />
          </div>
          {isResultMoreThanPage && (
            <div className="mb-m">
              <CustomPagination
                metadata={metadata}
                pageSize={pageSize}
                total={metadata?.total_results || 0}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            </div>
          )}
        </>
      )}
      {open && <ReservationDetailModal open={open} bookingUuid={selectedBookingId} onClose={handleOnCancel} />}
    </>
  )
}

BookingTable.propTypes = {
  page: PropTypes.number,
  pageSize: PropTypes.number,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.string,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  metadata: PropTypes.object,
  items: PropTypes.array,
  setIsAppend: PropTypes.func
}

export default BookingTable
